import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { AppButton } from "site/ui/base/Button";
import { useNavigate } from "react-router-dom";
import { routeNames } from "utils";
import { useBreakpoint } from "hooks/media/useBreakpoint";

const Container = styled.section`
  margin: 0 auto;
  padding: 4rem 1rem;
  max-width: 100%; // 1200px;
  font-family: "Manrope", sans-serif;
`;

const Wrapper = styled.div`
  gap: 3rem;
  align-items: center;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const ImageContainer = styled(motion.div)`
  border-radius: 12px;
  overflow: hidden;

  img {
    // width: 100%;
    height: auto;
    display: block;
  }
`;

const ContentContainer = styled(motion.div)`
  padding: 1rem;
`;

const Title = styled.h2`
  font-size: 2rem;
  color: #102a8e;
  margin-bottom: 1.5rem;
  font-weight: bold;
`;

const Description = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 2rem;
  font-size: 14px;
`;

const FeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 2rem 0;
`;

const FeatureItem = styled(motion.li)`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  font-size: 14px;
  color: #333;

  svg {
    margin-right: 12px;
    flex-shrink: 0;
  }
`;

const CheckIcon = styled.div`
  width: 20px;
  height: 20px;
  margin-right: 12px;
  color: #5575f7;
  flex-shrink: 0;
`;

export const Whychooseus: React.FC = () => {
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const features = [
    "Designed Around Real Recruitment Decisions",
    "Focused on Interviews, Shortlisting and Professional Judgement",
    "Tailored Guidance for Healthcare Professionals",
    "Practical Accessibility Preparation Without Unnecessary Complexity",
    "Clear Actionable Resources That Build Interview Confidence",
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  return (
    <Container>
      <Wrapper>
        <ImageContainer
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
        >
          <img
            src="/assets/medical-professional.png"
            alt="Healthcare professional using medical equipment"
          />
        </ImageContainer>

        <ContentContainer
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          <Title>Why Healthcare Professionals Choose Compelling Candidature</Title>
          <Description>
            Compelling Candidature is designed for healthcare professionals who want clarity, confidence, 
            and structure when navigating applications and interviews within the UK healthcare system.
          </Description>

          <FeatureList>
            {features.map((feature, index) => (
              <FeatureItem key={index} variants={itemVariants}>
                <CheckIcon>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.5 10.625L9.375 12.5L12.5 8.125M17.5 10C17.5 11.0567 16.975 11.9917 16.1725 12.5567C16.259 13.0506 16.2251 13.5582 16.0738 14.0363C15.9225 14.5144 15.6582 14.949 15.3033 15.3033C14.949 15.6582 14.5144 15.9225 14.0363 16.0738C13.5582 16.2251 13.0506 16.259 12.5567 16.1725C12.2685 16.5829 11.8857 16.9178 11.4407 17.1489C10.9956 17.38 10.5015 17.5004 10 17.5C8.94333 17.5 8.00833 16.975 7.44333 16.1725C6.94936 16.2589 6.44184 16.225 5.96374 16.0737C5.48564 15.9224 5.05102 15.6582 4.69667 15.3033C4.34177 14.949 4.07748 14.5144 3.92619 14.0363C3.77489 13.5582 3.74104 13.0506 3.8275 12.5567C3.4171 12.2685 3.08218 11.8857 2.85111 11.4407C2.62003 10.9956 2.49959 10.5015 2.5 10C2.5 8.94333 3.025 8.00833 3.8275 7.44333C3.74104 6.94936 3.77489 6.44182 3.92619 5.96371C4.07748 5.48559 4.34177 5.05098 4.69667 4.69667C5.05102 4.34183 5.48564 4.07758 5.96374 3.92629C6.44184 3.77499 6.94936 3.74111 7.44333 3.8275C7.73154 3.41715 8.11435 3.08227 8.55938 2.8512C9.00441 2.62013 9.49856 2.49966 10 2.5C11.0567 2.5 11.9917 3.025 12.5567 3.8275C13.0506 3.74111 13.5582 3.77499 14.0363 3.92629C14.5144 4.07758 14.949 4.34183 15.3033 4.69667C15.6582 5.05102 15.9224 5.48564 16.0737 5.96374C16.225 6.44184 16.2589 6.94936 16.1725 7.44333C16.5829 7.73149 16.9178 8.11429 17.1489 8.55933C17.38 9.00437 17.5004 9.49854 17.5 10Z"
                      stroke="#5575F7"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </CheckIcon>
                {feature}
              </FeatureItem>
            ))}
          </FeatureList>

          <AppButton fullWidth variant="primary" onClick={() => navigate(routeNames.guide)}>
            {isMobile ? "Explore" : "Explore how we support your career"}
          </AppButton>
        </ContentContainer>
      </Wrapper>
    </Container>
  );
};
